import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useResendOrderEmail } from '../hooks/use-order-mutations';
import { resendErrorKey } from '../schemas/order-rules';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ResendEmailDialogProps {
  storeId: string;
  order: Pick<Order, 'id' | 'name' | 'email'>;
  templateKey: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Sends one of the order's emails again, from the latest occurrence of its event, even when the
 * template is switched off. A walk-in order has no email, so an address is required there.
 */
export function ResendEmailDialog({
  storeId,
  order,
  templateKey,
  onOpenChange,
}: ResendEmailDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const resend = useResendOrderEmail(storeId, order.id);
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const needsAddress = order.email === null;
  const label = t(`orders:notifications.event.${templateKey}`, { defaultValue: templateKey });

  const submit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldError(null);
    const address = to.trim();
    if (needsAddress && address === '') {
      setFieldError(t('orders:resend.validation.required'));
      return;
    }
    if (address !== '' && !EMAIL.test(address)) {
      setFieldError(t('orders:resend.validation.email'));
      return;
    }
    try {
      await resend.mutateAsync({ templateKey, ...(address ? { to: address } : {}) });
      onOpenChange(false);
    } catch (caught) {
      const onTo = isApiError(caught)
        ? caught.details.find((d) => d.field === 'body.to')?.message
        : undefined;
      if (onTo) {
        setFieldError(onTo);
        return;
      }
      const key = resendErrorKey(caught);
      setError(key ? t(key.key, key.values) : message(caught));
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('orders:resend.title', { email: label })}</DialogTitle>
          <DialogDescription>{t('orders:resend.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {error ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="resend-to">{t('orders:resend.to')}</Label>
            <Input
              id="resend-to"
              type="email"
              autoComplete="off"
              value={to}
              placeholder={order.email ?? undefined}
              aria-invalid={fieldError ? true : undefined}
              onChange={(e) => setTo(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {needsAddress
                ? t('orders:resend.toRequiredHint')
                : t('orders:resend.toHint', { email: order.email })}
            </p>
            {fieldError ? (
              <p role="alert" className="text-destructive text-sm">
                {fieldError}
              </p>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">{t('orders:resend.limit')}</p>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={resend.isPending}>
              {resend.isPending ? t('orders:resend.submitting') : t('orders:resend.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
